import { EventEmitter, Injectable } from '@angular/core';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { BrowserStorage } from 'src/app/models/browserStorage';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class GetLanguageService {

    private currentLanguageSubject = new BehaviorSubject<string>(BrowserStorage.get('language') || 'en');
    language$ = this.currentLanguageSubject.asObservable();
    get currentLanguage(): string {
        return this.currentLanguageSubject.getValue();
    }
    get translationsLoaded(): boolean {
        return BrowserStorage.get('translationsLoaded') === 'true';
    }
    languageChanged = new BehaviorSubject<string>(null);
    constructor(
        private http: HttpClient,
        private fireStorage: AngularFireStorage,
        private translateService: TranslateService,
    ) {
        if (this.currentLanguage) {
            this.languageChanged.next(this.currentLanguage);
        }
     }

    async getJsonData() {
        const lang = this.currentLanguage;
        const cachedJson = BrowserStorage.get(`${lang}.json`);
        const cachedVersion = BrowserStorage.get(`${lang}.version`);
        const jsonFileRef = this.fireStorage.refFromURL(`${environment.firebase.langsURL}`);

        let languageReady = false;

        if (cachedJson) {
            const parsed = JSON.parse(cachedJson);
            this.translateService.setTranslation(lang, parsed);
            this.translateService.use(lang);
            languageReady = true;
        }

        try {
            const fileList = await firstValueFrom(jsonFileRef.listAll());

            for (const file of fileList.items) {
            if (file.name === `${lang}.json`) {
                const url = await file.getDownloadURL();
                const jsonData: any = await firstValueFrom(this.http.get(url));
                const newVersion = jsonData.version;

                if (!cachedJson || !cachedVersion || cachedVersion !== newVersion) {
                const copy = { ...jsonData };
                delete copy.version;

                BrowserStorage.set(`${lang}.json`, JSON.stringify(copy));
                BrowserStorage.set(`${lang}.version`, newVersion);
                this.translateService.setTranslation(lang, copy);

                if (!languageReady) {
                    this.translateService.use(lang);
                }
                }

                BrowserStorage.set('translationsLoaded', true);
                break;
            }
            }
        } catch (error) {
            console.error('Error al cargar traducciones:', error);
        }
    }

    initTranslations(): () => Promise<void> {
        return async () => {
            const lang = this.currentLanguage;
            const jsonFileRef = this.fireStorage.refFromURL(`${environment.firebase.langsURL}`);
            const cachedJson = BrowserStorage.get(`${lang}.json`);
            const cachedVersion = BrowserStorage.get(`${lang}.version`);

            let languageReady = false;

            if (cachedJson) {
                const parsed = JSON.parse(cachedJson);
                this.translateService.setTranslation(lang, parsed);
                this.translateService.use(lang);
                languageReady = true;
            }

            try {
                const fileList = await firstValueFrom(jsonFileRef.listAll());
                const file = fileList.items.find(f => f.name === `${lang}.json`);
                if (!file) {
                    console.warn(`File ${lang}.json not found in storage.`);
                    return;
                }
                const url = (await file.getDownloadURL()) + (environment.production ? '' : `?ts=${Date.now()}`);
                const jsonData: any = await firstValueFrom(this.http.get(url));
                const newVersion = jsonData.version;

                if (!cachedVersion || cachedVersion !== newVersion) {
                    const copy = { ...jsonData };
                    delete copy.version;

                    BrowserStorage.set(`${lang}.json`, JSON.stringify(copy));
                    BrowserStorage.set(`${lang}.version`, newVersion);
                    this.translateService.setTranslation(lang, copy);
                    if (!languageReady) this.translateService.use(lang);
                }
            } catch (error) {
                console.error(error);
            }
        };
    }

    changeLanguage(language: string) {
        this.currentLanguageSubject.next(language);
        BrowserStorage.set('language', language);
        this.getJsonData();
        this.translateService.use(language);
        this.languageChanged.next(language);
    }

}
